import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WorkOutline as CareerIcon,
  FavoriteBorder as LoveIcon,
  LocalHospital as HealthIcon,
  AccountBalanceWallet as FinanceIcon,
} from '@mui/icons-material';
import { RashiInfo, RashiPhalResponse, rashiPhalApi } from '../services/api';

const ELEMENT_COLORS: Record<string, string> = {
  Fire: '#E53935',
  Earth: '#43A047',
  Water: '#1E88E5',
  Air: '#FDD835',
};

const RashiCard: React.FC<{ rashi: RashiInfo }> = ({ rashi }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: expanded ? '2px solid' : '1px solid',
        borderColor: expanded ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardActionArea onClick={() => setExpanded(!expanded)} sx={{ p: 0 }}>
        <CardContent sx={{ pb: expanded ? 0 : undefined }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: { xs: 32, md: 40 } }}>{rashi.symbol}</Typography>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {rashi.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {rashi.nameTelugu}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={rashi.element}
                size="small"
                sx={{
                  bgcolor: ELEMENT_COLORS[rashi.element] + '18',
                  color: ELEMENT_COLORS[rashi.element],
                  fontWeight: 600,
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded}>
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={rashi.element} size="small" sx={{ bgcolor: ELEMENT_COLORS[rashi.element] + '18', color: ELEMENT_COLORS[rashi.element], fontWeight: 600, display: { sm: 'none' } }} />
            <Chip label={`Ruler: ${rashi.rulingPlanet} (${rashi.rulingPlanetTelugu})`} size="small" variant="outlined" />
            <Chip label={`Lucky #${rashi.prediction.luckyNumber}`} size="small" color="secondary" />
            <Chip label={`🎨 ${rashi.prediction.luckyColor}`} size="small" variant="outlined" />
          </Box>

          {[
            { icon: <CareerIcon />, label: 'Career', text: rashi.prediction.career },
            { icon: <LoveIcon />, label: 'Love', text: rashi.prediction.love },
            { icon: <HealthIcon />, label: 'Health', text: rashi.prediction.health },
            { icon: <FinanceIcon />, label: 'Finance', text: rashi.prediction.finance },
          ].map((section) => (
            <Box key={section.label} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ color: 'primary.main' }}>{section.icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {section.label}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 4 }}>
                {section.text}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Collapse>
    </Card>
  );
};

const RashiPhal: React.FC = () => {
  const [data, setData] = useState<RashiPhalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await rashiPhalApi.getToday();
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load daily horoscope. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const updatedDate = data
    ? new Date(data.lastUpdated).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Calculating planetary transits and generating today's predictions...
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          This may take a moment on the first load of the day
        </Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Alert severity="error">{error || 'Unable to load horoscope data.'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' } }}>
          రాశి ఫలం — Daily Horoscope
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
          Personalized daily predictions based on your Moon sign (చంద్ర రాశి) and today's planetary transits
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Don't know your Moon sign?{' '}
          <Typography component="a" href="/kundali" variant="body2" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}>
            Generate your Kundali
          </Typography>{' '}
          to find out.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Updated: ${updatedDate}`} size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
          {data.source === 'cached_fallback' && (
            <Chip label="Using cached predictions" size="small" color="warning" variant="outlined" />
          )}
        </Box>
      </Box>

      {/* Planetary Transits Summary */}
      {data.transits && data.transits.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#faf5ef' }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
              🪐 Today's Planetary Transits (Sidereal)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {data.transits.map((t) => (
                <Chip
                  key={t.planet}
                  label={`${t.planet}: ${t.sign} ${t.degree}°`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {data.rashis.map((rashi) => (
          <Grid size={{ xs: 12, sm: 6 }} key={rashi.id}>
            <RashiCard rashi={rashi} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          These predictions are general guidance based on your Sun sign. For personalized readings based on your complete birth chart,{' '}
          <Typography
            component="a"
            href="/book-slot"
            variant="body2"
            sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}
          >
            book a consultation
          </Typography>
          .
        </Typography>
      </Box>
    </Container>
  );
};

export default RashiPhal;
